import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import '../static/css/SettingsPage.css';
import '../static/css/ChangeEmail.css';

const eyeIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const eyeOffIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);

class ChangeEmail extends Component {
    constructor() {
        super();
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;
        this.state = {
            currentEmail: (user && user.email) || '',
            providerType: (user && user.provider_type) || null,
            newEmail: '',
            confirmNewEmail: '',
            currentPassword: '',
            showPassword: false,
            submitting: false,
            error: '',
            showSuccessModal: false,
            updatedEmail: '',
            resending: false,
            resendMessage: '',
            resendError: '',
        };
        this.onChangeNewEmail = this.onChangeNewEmail.bind(this);
        this.onChangeConfirmNewEmail = this.onChangeConfirmNewEmail.bind(this);
        this.onChangeCurrentPassword = this.onChangeCurrentPassword.bind(this);
        this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onResend = this.onResend.bind(this);
        this.closeSuccessModal = this.closeSuccessModal.bind(this);
    }

    closeSuccessModal() {
        this.setState({
            showSuccessModal: false,
            resendMessage: '',
            resendError: '',
        });
    }

    onChangeNewEmail(e) {
        this.setState({ newEmail: e.target.value, error: '' });
    }

    onChangeConfirmNewEmail(e) {
        this.setState({ confirmNewEmail: e.target.value, error: '' });
    }

    onChangeCurrentPassword(e) {
        this.setState({ currentPassword: e.target.value, error: '' });
    }

    togglePasswordVisibility() {
        this.setState((prev) => ({ showPassword: !prev.showPassword }));
    }

    isFormValid() {
        const { newEmail, confirmNewEmail, currentPassword, currentEmail } = this.state;
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        const validNewEmail = emailRegex.test(newEmail);
        const validConfirmEmail = emailRegex.test(confirmNewEmail);
        const emailsMatch = newEmail.trim().toLowerCase() === confirmNewEmail.trim().toLowerCase();
        const validPassword = currentPassword.length > 0;
        const notSame = newEmail.trim().toLowerCase() !== (currentEmail || '').trim().toLowerCase();
        return validNewEmail && validConfirmEmail && emailsMatch && validPassword && notSame;
    }

    async onSubmit(e) {
        e.preventDefault();
        if (!this.isFormValid() || this.state.submitting) return;
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ error: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ submitting: true, error: '' });
        try {
            const newEmailTrimmed = this.state.newEmail.trim();
            await axios.post(
                process.env.REACT_APP_SERVER_API + '/profile/email-update',
                { new_email: newEmailTrimmed, confirm_password: this.state.currentPassword },
                { headers: { Authorization: 'Bearer ' + token } }
            );
            try {
                const sessionResponse = await axios.post(
                    process.env.REACT_APP_SERVER_API + '/auth/web-session',
                    {},
                    { headers: { Authorization: 'Bearer ' + token } }
                );
                const { user, upcoming_event } = sessionResponse.data.data;
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('upcoming_event', JSON.stringify(upcoming_event));
            } catch (sessionErr) {
                const userJson = localStorage.getItem('user');
                if (userJson) {
                    const user = JSON.parse(userJson);
                    user.email = newEmailTrimmed;
                    user.is_verified = false;
                    localStorage.setItem('user', JSON.stringify(user));
                }
            }
            const userJson = localStorage.getItem('user');
            const refreshedUser = userJson ? JSON.parse(userJson) : null;
            this.setState({
                submitting: false,
                showSuccessModal: true,
                updatedEmail: newEmailTrimmed,
                currentEmail: (refreshedUser && refreshedUser.email) || newEmailTrimmed,
                newEmail: '',
                confirmNewEmail: '',
                currentPassword: '',
                error: '',
                resendMessage: '',
                resendError: '',
            });
        } catch (err) {
            const status = err.response && err.response.status;
            const data = err.response && err.response.data;
            const serverError = data && (data.error || (typeof data === 'string' ? data : ''));
            let message = 'Something went wrong. Please try again.';
            if (status === 401) {
                message = 'Wrong password.';
            } else if (status === 404 && serverError) {
                if (serverError.toLowerCase().includes('already in use')) {
                    message = 'That email is already in use.';
                } else if (serverError.toLowerCase().includes('already your current')) {
                    message = "That's already your current email.";
                } else {
                    message = serverError;
                }
            } else if (status === 400 && serverError) {
                message = serverError;
            }
            this.setState({ submitting: false, error: message });
        }
    }

    async onResend() {
        if (this.state.resending) return;
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ resendError: 'Your session has expired. Please sign in again.' });
            return;
        }
        this.setState({ resending: true, resendMessage: '', resendError: '' });
        try {
            const response = await axios.post(
                process.env.REACT_APP_SERVER_API + '/auth/resend-verification-email',
                {},
                { headers: { Authorization: 'Bearer ' + token } }
            );
            const message = (response.data && response.data.message) || 'Verification email sent.';
            this.setState({ resending: false, resendMessage: message });
        } catch (err) {
            this.setState({ resending: false, resendError: 'Could not send verification email. Please try again.' });
        }
    }

    renderForm() {
        const { showPassword, submitting, currentEmail, newEmail, confirmNewEmail, currentPassword, error } = this.state;
        const valid = this.isFormValid();
        const buttonDisabled = !valid || submitting;
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        const showMismatch =
            newEmail.length > 0 &&
            confirmNewEmail.length > 0 &&
            newEmail.trim().toLowerCase() !== confirmNewEmail.trim().toLowerCase();
        const showInvalidConfirm =
            confirmNewEmail.length > 0 &&
            !emailRegex.test(confirmNewEmail) &&
            !showMismatch;
        return (
            <form className="change-email-form" onSubmit={this.onSubmit}>
                <p className="change-email-current">
                    Current email: <span className="change-email-current-value">{currentEmail || '—'}</span>
                </p>
                <label className="change-email-form-label" htmlFor="change-email-new">New email</label>
                <input
                    id="change-email-new"
                    type="email"
                    className="change-email-form-input"
                    value={newEmail}
                    onChange={this.onChangeNewEmail}
                    autoComplete="email"
                />
                <label className="change-email-form-label" htmlFor="change-email-confirm">Confirm new email</label>
                <input
                    id="change-email-confirm"
                    type="email"
                    className="change-email-form-input"
                    value={confirmNewEmail}
                    onChange={this.onChangeConfirmNewEmail}
                    autoComplete="email"
                />
                {showMismatch && (
                    <p className="change-email-field-hint">Emails don't match.</p>
                )}
                {showInvalidConfirm && (
                    <p className="change-email-field-hint">Enter a valid email address.</p>
                )}
                <p className="change-email-password-note">Enter your password to update email.</p>
                <label className="change-email-form-label" htmlFor="change-email-password">Current password</label>
                <div className="change-email-form-input-wrapper">
                    <input
                        id="change-email-password"
                        type={showPassword ? 'text' : 'password'}
                        className="change-email-form-input"
                        value={currentPassword}
                        onChange={this.onChangeCurrentPassword}
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        className="change-email-form-toggle-password"
                        onClick={this.togglePasswordVisibility}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? eyeIcon : eyeOffIcon}
                    </button>
                </div>
                {error && <p className="change-email-error">{error}</p>}
                <button type="submit" className="change-email-form-submit" disabled={buttonDisabled}>
                    {submitting ? 'Updating…' : 'Update Email'}
                </button>
            </form>
        );
    }

    renderProviderLocked() {
        const { providerType, currentEmail } = this.state;
        const providerLabel = providerType === 'APPLE' ? 'Apple' : 'Google';
        return (
            <div className="change-email-locked">
                <p className="change-email-current">
                    Current email: <span className="change-email-current-value">{currentEmail || '—'}</span>
                </p>
                <p className="change-email-locked-message">
                    Your email is managed by your <span className="change-email-locked-provider">{providerLabel}</span> account.
                    Update it on {providerLabel} and it'll sync the next time you sign in to AniMates.
                </p>
                <p className="change-email-locked-secondary">
                    Want to switch to email + password sign-in instead? We'll be adding that option soon.
                </p>
            </div>
        );
    }

    renderSuccessModal() {
        const { updatedEmail, resending, resendMessage, resendError } = this.state;
        return (
            <div
                className="change-email-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="change-email-modal-title"
            >
                <div className="change-email-modal-card">
                    <h3 id="change-email-modal-title" className="change-email-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Email Updated</h3>
                    <p className="change-email-success">
                        Your email was updated to <span className="change-email-success-value">{updatedEmail}</span>.
                        We sent a verification link there — check your inbox to finish verifying.
                    </p>
                    <button
                        type="button"
                        className="change-email-resend-button"
                        onClick={this.onResend}
                        disabled={resending}
                    >
                        {resending ? 'Sending…' : 'Resend verification email'}
                    </button>
                    {resendMessage && <p className="change-email-resend-status">{resendMessage}</p>}
                    {resendError && <p className="change-email-error">{resendError}</p>}
                    <button
                        type="button"
                        className="change-email-modal-okay"
                        onClick={this.closeSuccessModal}
                        autoFocus
                    >
                        Okay
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { providerType } = this.state;
        const isProviderLocked = providerType === 'APPLE' || providerType === 'GOOGLE';
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content change-email-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Change Email</u></h2>
                        {isProviderLocked ? this.renderProviderLocked() : this.renderForm()}
                    </div>
                    {this.state.showSuccessModal && this.renderSuccessModal()}
                </div>
            </AuthGate>
        );
    }
}

export default ChangeEmail;
